/**
 * 69.2e (Foothold's intel loop; DIRECTION §0.8's 69.2): a scout's end — Go ▸ Scout there (a `move` with `scout: true`,
 * 69.2d) is done when the squad has seen its place: a member within SCOUT.seePeople of it whose eye has a line to it. The
 * task leaves the squad (its base order again) and the squad reads *scouted* — its report — until its next order.
 *
 * With 69.2a's rough circles on (GameConfig.roughStart, core/objectiveIntel.ts) a scout also ends when the circle under its
 * place resolves for its side (the report is the resolved objective); off, the exact place's rule alone. Pure; stepped once
 * a second (match/rates.ts), after the circles.
 */
import type { GameState, MatchSquad, Vec3 } from '../types';
import { eyePointAt } from './los';
import { personLineOfSight } from './fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecDot, vecNormalize } from '../math';
import { dacos } from './dmath';
import { circleUnder } from './objectiveIntel';

/** How near (people) a member must stand to its scout's place, with a line to it, to have seen it. */
export const SCOUT = { seePeople: 4 };

/** Has this squad seen the place (a living member within reach of it, its eye with a line to a person standing there)? */
function seen(state: GameState, sq: MatchSquad, place: { planetIndex: number; surfaceDir: Vec3 }): boolean {
  const planet = state.planets[place.planetIndex];
  if (!planet) return false;
  const ps = personSetOf(state), reach = SCOUT.seePeople * PERSON_BODY_HEIGHT * ps.k;
  const there = eyePointAt(planet, vecNormalize(place.surfaceDir), ps.losEyeHeight);   // a person standing there (low over the ground the fine ground hides it)
  return sq.unitIds.some(id => {
    const u = state.players.find(p => p.id === id);
    if (!u?.alive || u.planetIndex !== place.planetIndex) return false;
    const arc = dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(u.surfaceDir), vecNormalize(place.surfaceDir))))) * planet.radius;
    return arc <= reach && personLineOfSight(eyePointAt(planet, vecNormalize(u.surfaceDir), ps.losEyeHeight), there, state);
  });
}

/** Every squad's scout that has seen its place ends; the squad reads scouted until its next order. */
export function stepScouts(state: GameState, tick: number): GameState {
  if (!state.squads?.some(q => q.tasks.some(t => t.order.kind === 'move' && t.order.scout))) return state;
  let changed = false;
  const squads = state.squads.map(sq => {
    const done = sq.tasks.filter(t => t.order.kind === 'move' && t.order.scout && (t.expires === undefined || t.expires > tick) && t.order.planetIndex !== undefined
      && (seen(state, sq, { planetIndex: t.order.planetIndex, surfaceDir: t.order.surfaceDir! })
        || (!!state.config.roughStart && !!circleUnder(state, sq.team, { planetIndex: t.order.planetIndex, surfaceDir: t.order.surfaceDir! }, tick)?.resolved)));
    if (!done.length) return sq;
    changed = true;
    return { ...sq, tasks: sq.tasks.filter(t => !done.includes(t)), scouted: true as const };
  });
  return changed ? { ...state, squads } : state;
}
